import { useEffect, useRef, useState } from 'react';
import type { ReactNode } from 'react';
import {
  Building2,
  FileText,
  Globe,
  History,
  Mail,
  MapPin,
  Navigation,
  Package,
  Phone,
  UserX,
  Users,
  X,
} from 'lucide-react';
import { twMerge } from 'tailwind-merge';

import { UserAvatar } from '@/components/user-avatar';
import { AmazonPointPanel } from './detail/sections/amazon-point-panel';
import { useGetDispatchPlaceHistory, useGetDispatchPlaceUsers } from '@/features/dispatch-places/dispatch-places.queries';
import type { DispatchPlace, DispatchPlaceHistoryItem, DispatchPlaceStatus, DispatchPlaceType, DispatchPlaceUser } from '@/features/dispatch-places/dispatch-places.types';
import { formatDateTime } from '@/utils/format';
import { VITE_MAPBOX_TOKEN } from '@/utils/envs';
import type { MapboxInstance, MapboxMap } from '@/utils/mapbox-cdn.types';

const MAPBOX_VERSION = '3.12.0';
const MAPBOX_CDN_BASE = `https://api.mapbox.com/mapbox-gl-js/v${MAPBOX_VERSION}`;

function loadMapboxFromCDN(): Promise<MapboxInstance> {
  return new Promise((resolve) => {
    if (window.mapboxgl) {
      resolve(window.mapboxgl);
      return;
    }

    const existingLink = document.getElementById('mapbox-css');
    if (!existingLink) {
      const link = document.createElement('link');
      link.id = 'mapbox-css';
      link.rel = 'stylesheet';
      link.href = `${MAPBOX_CDN_BASE}/mapbox-gl.css`;
      document.head.appendChild(link);
    }

    const script = document.createElement('script');
    script.src = `${MAPBOX_CDN_BASE}/mapbox-gl.js`;
    script.onload = () => resolve(window.mapboxgl as MapboxInstance);
    document.head.appendChild(script);
  });
}

// ── Label maps ────────────────────────────────────────────────────────────────

const TYPE_LABEL: Record<DispatchPlaceType, string> = {
  physical: 'Físico',
  locker: 'Locker',
  virtual: 'Virtual',
};

const STATUS_LABEL: Record<DispatchPlaceStatus, string> = {
  active: 'Activo',
  inactive: 'Inactivo',
  pending: 'Pendiente',
};

const STATUS_BADGE_CLASS: Record<DispatchPlaceStatus, string> = {
  active: 'badge-success',
  inactive: 'badge-error',
  pending: 'badge-warning',
};

// ── Tab definitions ───────────────────────────────────────────────────────────

type TabId = 'general' | 'users' | 'schedules' | 'billing' | 'amazon' | 'history';

interface Tab {
  id: TabId;
  label: string;
}

const TABS: Tab[] = [
  { id: 'general', label: 'Datos generales' },
  { id: 'users', label: 'Usuarios' },
  { id: 'schedules', label: 'Horarios' },
  { id: 'billing', label: 'Datos de facturación' },
  { id: 'amazon', label: 'Amazon Hub' },
  { id: 'history', label: 'Historial' },
];

const WEEK_DAYS = [
  'Lunes',
  'Martes',
  'Miércoles',
  'Jueves',
  'Viernes',
  'Sábado',
  'Domingo',
];

// ── Sub-components ────────────────────────────────────────────────────────────

interface InfoRowProps {
  icon: ReactNode;
  label: string;
  value: string | null | undefined;
  col_span_full?: boolean;
}

function InfoRow(props: InfoRowProps) {
  return (
    <div className={twMerge('flex flex-col gap-0.5', props.col_span_full && 'col-span-2')}>
      <div className="flex items-center gap-1.5 text-xs text-base-content/50">
        <span className="shrink-0">{props.icon}</span>
        {props.label}
      </div>
      <span className="text-sm font-medium break-words">{props.value ?? '—'}</span>
    </div>
  );
}

interface BillingRowProps {
  label: string;
  value: string | null | undefined;
}

function BillingRow(props: BillingRowProps) {
  return (
    <div className="flex flex-col gap-0.5">
      <span className="text-xs text-base-content/50">{props.label}</span>
      <span className="text-sm font-medium">{props.value ?? '—'}</span>
    </div>
  );
}

// ── Mini map ──────────────────────────────────────────────────────────────────

interface MiniMapProps {
  coordinates: [number, number];
}

function MiniMap(props: MiniMapProps) {
  const container_ref = useRef<HTMLDivElement>(null);
  const map_ref = useRef<MapboxMap | null>(null);

  useEffect(() => {
    if (!container_ref.current || map_ref.current) return;

    let cancelled = false;

    loadMapboxFromCDN().then((mapboxgl) => {
      if (cancelled || !container_ref.current) return;

      mapboxgl.accessToken = VITE_MAPBOX_TOKEN;

      map_ref.current = new mapboxgl.Map({
        container: container_ref.current,
        style: 'mapbox://styles/mapbox/light-v11',
        center: props.coordinates,
        zoom: 15,
        interactive: false,
      });
    });

    return () => {
      cancelled = true;
      map_ref.current?.remove();
      map_ref.current = null;
    };
  // The coordinates for a given place never change within this drawer instance.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <div
      ref={container_ref}
      className="w-full rounded-lg overflow-hidden border border-base-300"
      style={{ height: 160 }}
    />
  );
}

// ── Tab panels ────────────────────────────────────────────────────────────────

interface GeneralTabProps {
  place: DispatchPlace;
}

function GeneralTab(props: GeneralTabProps) {
  const place = props.place;

  const full_address =
    place.address
      ? `${place.address.street} ${place.address.number}, ${place.address.neighborhood}, ${place.address.city}, ${place.address.state} CP ${place.address.postal_code}`
      : null;

  return (
    <div className="flex flex-col gap-5">
      {/* ── Identity ── */}
      <div className="flex items-center gap-3">
        <UserAvatar name={place.name} picture={undefined} size={56} />
        <div className="flex flex-col gap-1 min-w-0">
          <span className="text-base font-bold leading-tight truncate">{place.name}</span>
          <span className={twMerge('badge badge-sm', STATUS_BADGE_CLASS[place.status])}>
            {STATUS_LABEL[place.status]}
          </span>
        </div>
      </div>

      {/* ── Stats ── */}
      <div className="grid grid-cols-5 gap-2">
        {(
          [
            { label: 'Por recibir', value: 0 },
            { label: 'Recolectados', value: 0 },
            { label: 'Entregados', value: 0 },
            { label: 'Por devolver', value: 0 },
            { label: 'Cancelados', value: 0 },
          ] as const
        ).map((stat) => (
          <div
            key={stat.label}
            className="flex flex-col items-center gap-0.5 rounded-lg border border-base-300 bg-base-200 p-2 text-center"
          >
            <span className="text-lg font-bold leading-none">{stat.value}</span>
            <span className="text-[10px] text-base-content/50 leading-tight">{stat.label}</span>
          </div>
        ))}
      </div>

      {/* ── Mini map ── */}
      {place.location && (
        <MiniMap coordinates={place.location.coordinates} />
      )}

      {/* ── Info grid ── */}
      <div className="grid grid-cols-2 gap-x-4 gap-y-4">
        <InfoRow
          icon={<MapPin size={12} />}
          label="Nombre del punto"
          value={place.name}
        />
        <InfoRow
          icon={<Building2 size={12} />}
          label="Tipo"
          value={TYPE_LABEL[place.type] ?? place.type}
        />
        <InfoRow
          icon={<Globe size={12} />}
          label="País"
          value={place.country_code.toUpperCase()}
        />
        <InfoRow
          icon={<Package size={12} />}
          label="Capacidad"
          value={String(place.capacity)}
        />
        <InfoRow
          icon={<FileText size={12} />}
          label="Descripción"
          value={place.description || null}
          col_span_full
        />
        <InfoRow
          icon={<Navigation size={12} />}
          label="Dirección"
          value={full_address}
          col_span_full
        />
        <InfoRow
          icon={<Phone size={12} />}
          label="Teléfono"
          value={place.contact?.phone}
        />
        <InfoRow
          icon={<Mail size={12} />}
          label="Email"
          value={place.contact?.email}
        />
        <InfoRow
          icon={<Users size={12} />}
          label="Proveedor"
          value={place.provider?.name}
        />
      </div>
    </div>
  );
}

const USER_ROLE_LABELS: Record<string, string> = {
  'owner:all': 'Dueño',
  'owner': 'Dueño',
  'operator:all': 'Operador',
  'operator': 'Operador',
  'manager': 'Gerente',
  'viewer': 'Consultor',
  'admin': 'Administrador',
  'cashier': 'Cajero',
};

function resolveUserRole(roles: DispatchPlaceUser['roles'], kind: DispatchPlaceUser['kind']): string {
  if (roles.length > 0) {
    return roles.map((r) => USER_ROLE_LABELS[r] ?? r).join(', ');
  }
  if (kind === 'punto') return 'Operador';
  return kind || 'Sin rol';
}

interface UsersTabProps {
  dispatchPlaceId: string;
}

function UsersTab(props: UsersTabProps) {
  const { data, isLoading } = useGetDispatchPlaceUsers(props.dispatchPlaceId);
  const users = data ?? [];

  if (isLoading) {
    return (
      <div className="flex flex-col divide-y divide-base-200">
        {[0, 1, 2].map((i) => (
          <div key={i} className="flex items-center gap-3 py-3">
            <div className="skeleton h-2.5 w-2.5 rounded-full shrink-0" />
            <div className="flex-1 flex flex-col gap-1.5">
              <div className="skeleton h-3.5 w-32 rounded" />
              <div className="skeleton h-3 w-24 rounded" />
            </div>
            <div className="skeleton h-5 w-14 rounded-full" />
          </div>
        ))}
      </div>
    );
  }

  if (users.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-base-content/40">
        <UserX size={40} strokeWidth={1.5} />
        <span className="text-sm">Sin usuarios registrados</span>
      </div>
    );
  }

  return (
    <div className="flex flex-col divide-y divide-base-200">
      {users.map((user) => (
        <div key={user.id} className="flex items-center gap-3 py-3">
          <div
            className={twMerge(
              'h-2.5 w-2.5 rounded-full shrink-0',
              user.active ? 'bg-success' : 'bg-error',
            )}
            title={user.active ? 'Activo' : 'Inactivo'}
          />
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-none truncate">{user.name}</p>
            {user.phone && (
              <p className="mt-1 text-xs text-base-content/50">{user.phone}</p>
            )}
          </div>
          <span className="badge badge-ghost badge-sm whitespace-nowrap shrink-0">
            {resolveUserRole(user.roles, user.kind)}
          </span>
        </div>
      ))}
    </div>
  );
}

interface SchedulesTabProps {
  place: DispatchPlace;
}

function SchedulesTab(props: SchedulesTabProps) {
  void props.place; // reserved for when schedule shape is documented
  return (
    <div className="flex flex-col gap-4">
      <div className="overflow-x-auto rounded-lg border border-base-300">
        <table className="table table-sm w-full">
          <thead>
            <tr>
              <th>Día</th>
              <th className="text-center">Apertura</th>
              <th className="text-center">Cierre</th>
            </tr>
          </thead>
          <tbody>
            {WEEK_DAYS.map((day) => (
              <tr key={day}>
                <td className="font-medium">{day}</td>
                <td className="text-center text-base-content/40">—</td>
                <td className="text-center text-base-content/40">—</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <p className="text-xs text-base-content/40 text-center">
        Los horarios se configurarán cuando el endpoint esté disponible.
      </p>
    </div>
  );
}

interface BillingTabProps {
  place: DispatchPlace;
}

function BillingTab(props: BillingTabProps) {
  const billing = props.place.billing;
  const banking = props.place.banking;

  return (
    <div className="flex flex-col gap-6">
      {/* ── Fiscal data ── */}
      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-base-content/70">Datos fiscales</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-4">
          <BillingRow label="Tipo de RFC / Tax ID" value={billing?.tax_id_type?.toUpperCase()} />
          <BillingRow label="Valor de RFC / Tax ID" value={billing?.tax_id_value} />
          <BillingRow label="Razón social" value={billing?.legal_name} />
          <BillingRow label="Régimen fiscal" value={billing?.regime} />
        </div>
      </div>

      <div className="divider my-0" />

      {/* ── Banking data ── */}
      <div className="flex flex-col gap-3">
        <h3 className="text-sm font-semibold text-base-content/70">Datos bancarios</h3>
        <div className="grid grid-cols-2 gap-x-4 gap-y-4">
          <BillingRow label="Tipo de cuenta" value={banking?.account_id_type?.toUpperCase()} />
          <BillingRow label="Número de cuenta" value={banking?.account_id_value} />
          <BillingRow label="Banco" value={banking?.bank_name} />
          <BillingRow label="Titular" value={banking?.holder_name} />
        </div>
      </div>
    </div>
  );
}

const HISTORY_TYPE_LABELS: Record<string, string> = {
  general: 'Actualización general',
  capacity: 'Modificación de capacidad',
  status: 'Cambio de estado',
  address: 'Actualización de dirección',
  contact: 'Actualización de contacto',
  billing: 'Actualización de facturación',
  banking: 'Actualización bancaria',
  provider: 'Cambio de proveedor',
  location: 'Actualización de ubicación',
  schedule: 'Actualización de horario',
  creation: 'Creación del punto',
  update: 'Actualización',
};

function resolveResponsable(
  responsable: DispatchPlaceHistoryItem['responsable'],
): string {
  if (!responsable) return '—';
  if (responsable.name && responsable.name.trim() !== '') return responsable.name;
  if (responsable.email && responsable.email.trim() !== '') return responsable.email;
  return '—';
}

interface HistoryTabProps {
  dispatchPlaceId: string;
}

function HistoryTab(props: HistoryTabProps) {
  const { data, isLoading } = useGetDispatchPlaceHistory(props.dispatchPlaceId);
  const items = data?.items ?? [];

  if (isLoading) {
    return (
      <div className="ml-2 border-l-2 border-base-300 pl-5">
        {[0, 1, 2].map((i) => (
          <div key={i} className="pb-7 last:pb-0 relative">
            <div className="absolute -left-[27px] top-1 h-3 w-3 rounded-full ring-4 ring-base-100 bg-base-300" />
            <div className="flex flex-col gap-2">
              <div className="flex justify-between gap-2">
                <div className="skeleton h-3.5 w-40 rounded" />
                <div className="skeleton h-4 w-24 rounded" />
              </div>
              <div className="skeleton h-3 w-28 rounded" />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (items.length === 0) {
    return (
      <div className="flex flex-col items-center justify-center gap-3 py-16 text-base-content/40">
        <History size={40} strokeWidth={1.5} />
        <span className="text-sm">No hay movimientos en el historial.</span>
      </div>
    );
  }

  return (
    <div className="ml-2 border-l-2 border-base-300 pl-5">
      {items.map((item, index) => (
        <div
          key={`${item.type}-${item.changed_at}-${index}`}
          className="pb-7 last:pb-0 relative"
        >
          <div className="absolute -left-[27px] top-1 h-3 w-3 rounded-full ring-4 ring-base-100 bg-primary" />
          <div className="flex flex-col gap-1">
            <div className="flex items-start justify-between gap-2">
              <span className="text-sm font-bold leading-snug">{item.description}</span>
              <span className="shrink-0 whitespace-nowrap rounded bg-base-200 px-1.5 py-0.5 text-[10px] font-medium opacity-50">
                {formatDateTime(item.changed_at)}
              </span>
            </div>
            <span className="text-xs text-base-content/50">
              {HISTORY_TYPE_LABELS[item.type] ?? item.type}
            </span>
            <div className="flex items-center gap-1 text-xs text-base-content/60">
              <Users size={10} className="shrink-0" />
              <span>Por: {resolveResponsable(item.responsable)}</span>
            </div>
          </div>
        </div>
      ))}
    </div>
  );
}

// ── Props ─────────────────────────────────────────────────────────────────────

export interface DispatchPlaceDrawerProps {
  place: DispatchPlace | null;
  onClose: () => void;
}

// ── Drawer ────────────────────────────────────────────────────────────────────

/**
 * Slide-in drawer that shows the full detail of a dispatch place.
 * Renders nothing when `place` is null.
 */
export function DispatchPlaceDrawer(props: DispatchPlaceDrawerProps) {
  const [active_tab, setActiveTab] = useState<TabId>('general');

  // Reset to first tab whenever a different place is opened.
  useEffect(() => {
    if (props.place) {
      setActiveTab('general');
    }
  }, [props.place?.id]);

  if (!props.place) return null;

  const place = props.place;

  return (
    <>
      {/* ── Overlay ── */}
      <div
        className="fixed inset-0 z-40 bg-black/40"
        onClick={props.onClose}
        aria-hidden="true"
      />

      {/* ── Panel ── */}
      <div
        role="dialog"
        aria-modal="true"
        aria-label={`Detalle de ${place.name}`}
        className={twMerge(
          'fixed inset-y-0 right-0 z-50 flex w-[480px] max-w-full flex-col bg-base-100 shadow-2xl',
          'transition-transform duration-300 translate-x-0',
        )}
      >
        {/* ── Header ── */}
        <div className="flex items-center gap-2 border-b border-base-300 px-4 py-3 shrink-0">
          <button
            type="button"
            onClick={props.onClose}
            className="btn btn-ghost btn-sm btn-square"
            aria-label="Cerrar"
          >
            <X size={18} />
          </button>
          <div className="flex items-center gap-1.5 text-sm text-base-content/60 min-w-0">
            <span className="shrink-0">Detalle punto99</span>
            <span className="shrink-0">›</span>
            <span className="font-semibold text-base-content truncate">{place.name}</span>
          </div>
        </div>

        {/* ── Tabs ── */}
        <div className="flex gap-1 border-b border-base-300 px-3 pt-2 shrink-0 overflow-x-auto">
          {TABS.map((tab) => (
            <button
              key={tab.id}
              type="button"
              onClick={() => setActiveTab(tab.id)}
              className={twMerge(
                'shrink-0 rounded-t-md px-3 py-2 text-xs font-medium transition-colors',
                active_tab === tab.id
                  ? 'border-b-2 border-primary text-primary'
                  : 'text-base-content/60 hover:text-base-content',
              )}
            >
              {tab.label}
            </button>
          ))}
        </div>

        {/* ── Tab content ── */}
        <div className="flex-1 overflow-y-auto px-4 py-5">
          {active_tab === 'general' && <GeneralTab place={place} />}
          {active_tab === 'users' && <UsersTab dispatchPlaceId={place.id} />}
          {active_tab === 'schedules' && <SchedulesTab place={place} />}
          {active_tab === 'billing' && <BillingTab place={place} />}
          {active_tab === 'amazon' && <AmazonPointPanel place={place} />}
          {active_tab === 'history' && <HistoryTab dispatchPlaceId={place.id} />}
        </div>
      </div>
    </>
  );
}
